import React from "react";
import { render } from "react-dom";

import { AvatarComponentProps, AvatarSize, ControlComponentProps, LoaderType, Shape } from "@multiweb/reactComponents";
import { ControlConnector } from "@multiweb/reactConnectors/ControlConnector";
import { GenericControlConnectorAttrs } from "@multiweb/reactConnectors/GenericControlConnector";
import { IUser } from "@multiweb/types/contracts";
import { AppContext } from "../../../App";
import { DataConverter } from "../../../Global/Base/Converters/DataConverter";
import { IUserProfileData, IUserProfileDataNewData } from "../../../Global/Base/DataTypes/IUserProfileInterfaces";
import { FileStreamReader } from "../../../Global/Base/Streams/FileStreamReader";
import { FileUtils } from "../../../Global/Base/Utils/FileUtils";
import { OWObject } from "../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { FileUploader } from "../../../OW.Remoting/Remoting/FileUploader";
import { Localization } from "../../../OW.Remoting/Remoting/Localization";
import { OWManager } from "../../../OW.Remoting/Remoting/OWManager";
import { UserProfileManager } from "../../../OW.Remoting/Remoting/UserProfileManager";
import { LocalizedStringBuilder } from "../../UI/UI/DataBuilders/LocalizedStringBuilder";
import { MessageButtonType, MessageWindow } from "../../UI/UI/MessageWindow";

const EMPTY_AVATAR_PATH: string | null = null;
const IMAGE_FILE_EXTENSIONS: string[] = [
    "jpg", "jpeg", "jfif", "jpe", "jif", "jfi",
    "png",
    "gif",
    "svg", "svgz",
    "heif", "heic",
    "bmp", "dip",
    "webp",
    // "jp2", "j2k", "jpf", "jpx", "jpm", "mj2", // jpeg2000 je podporovan pouze na MacOS a Safari
];
const LOC_KEYS = {
    deleteButtonText: "GUI-780603", // Smazat obrázek
    changeButtonText: "GUI-780602", // Nahrát obrázek
    snapButtonText: "GUI-780604", // Vyfotit
    refreshButtonText: "GUI-781163", // Opakovat nahrávání
    uploadErrorText: "GUI-252346", // Chyba při nahrávání souboru
    loadingText: "GUI-176346", // Nahrávám
};

export class AvatarConnector extends ControlConnector {
    private __imgSrc: string | undefined = EMPTY_AVATAR_PATH;
    /** Název zobrazeného atributu */
    protected _attrName: string = "Data";
    private readonly _userId: number | undefined = undefined;
    private _currentUser: IUser | undefined = undefined;
    private __isLoading: boolean = false;
    private __isError: boolean = false;
    private __loaderType: LoaderType = LoaderType.spinner;
    private __actualObject: OWObject | null = null;
    private _avatarUrl: string | undefined = undefined;

    private _locTexts: { [key in keyof typeof LOC_KEYS]?: string } = {};

    private readonly _userInitials: string = "";
    private _uploadProgress: number = 0;

    private set _imgSrc(val: string | undefined) {
        if (this.__imgSrc === val) return;

        this.__imgSrc = val;
        this._render();
    }

    private get _imgSrc(): string | undefined {
        return this.__imgSrc;
    }

    private get _isLoading(): boolean {
        return this.__isLoading;
    }

    private set _isLoading(v: boolean) {
        if (v === this.__isLoading) return;
        this.__isLoading = v;
        this._render();
    }

    private set _loaderType(v: LoaderType) {
        if (v === this.__loaderType) return;
        this.__loaderType = v;
        this._render();
    }

    private get _loaderType(): LoaderType {
        return this.__loaderType;
    }

    private get _isError(): boolean {
        return this.__isError;
    }

    private set _isError(v: boolean) {
        if (v === this.__isError) return;
        this.__isError = v;
        this._render();
    }

    private get _actualObject(): OWObject | null {
        return this.__actualObject;
    }

    private set _actualObject(actualObject: OWObject | null) {
        this.__actualObject = actualObject;
        this._updatePreview();
    }

    constructor(attrs: GenericControlConnectorAttrs, canRender: boolean = true) {
        super(attrs, false);

        this._userId = DataConverter.toNumberOrUndefined(attrs.rootElement.getAttribute("data-userid"));
        this._currentUser = AppContext.instance.currentUser;

        this._isLoading = true;
        UserProfileManager.getUserProfileItem(this._userId, "Avatar", true).then((item) => {
            this._actualObject = item.object;
            this._isLoading = false;
        });

        if (!this._userId) {
            this._userInitials = (this._currentUser.fullName || this._currentUser.userName).split(" ").map(i => i[0]).join("");
            this._renderIfTrue(canRender);
        }

        attrs.rootElement.classList.add("AvatarConnector");

        this._loadLocalizations();
    }

    private async _loadLocalizations(): Promise<void> {
        const keys = Object.keys(LOC_KEYS) as (keyof typeof LOC_KEYS)[];
        const locKeys = Object.values(LOC_KEYS);

        const localizations = await Localization.getTexts(...locKeys);
        for (const key of keys) {
            this._locTexts[key] = localizations.get(LOC_KEYS[key]);
        }

        this._render();
    }

    private async _updatePreview(): Promise<void> {
        const url = await this._getAvatarUrl();
        if (url) {
            this._imgSrc = url;
        } else
            this._imgSrc = EMPTY_AVATAR_PATH;
    }

    protected override _getRenderProps(): AvatarComponentProps & ControlComponentProps {
        return {
            ...super._getRenderProps(),
            image: this.__imgSrc,
            isEditing: true,
            shape: Shape.circle,
            size: AvatarSize.large,
            snapBtnClick: this._capturePhoto.bind(this),
            refreshUploadClick: this._uploadFileCallback.bind(this),
            uploadFileClick: this._uploadFileCallback.bind(this),
            deleteImageClick: this._deleteAvatar.bind(this),
            isLoading: this._isLoading,
            loaderType: this._loaderType,
            loadingProgress: this._uploadProgress,
            userInitials: this._userInitials,
            deleteButtonText: this._locTexts?.deleteButtonText,
            snapButtonText: this._locTexts?.snapButtonText,
            changeButtonText: this._locTexts?.changeButtonText,
            refreshButtonText: this._locTexts?.refreshButtonText,
            loadingText: this._locTexts?.loadingText,
            isError: this._isError,
        };
    }
    /**
     * Metoda pro vykreslování react komponenty.
     * @protected
     */
    protected override _render(): void {
        const genericComponentProps = this._getRenderProps();
        render(
            <this._rootComponent>
                <this._component {...genericComponentProps} />
            </this._rootComponent>,
            this._rootElement,
        );
    }

    private async _capturePhoto(file: Blob | null): Promise<void> {
        if (!file) return;

        await this._uploadFile(new File([file], "snap.jpg", { lastModified: new Date().getTime(), type: file.type }));
    }

    private async _uploadFileCallback(file: readonly File[]): Promise<void> {
        if (file.length > 0) {
            await this._uploadFile(file[0]);
        }
    }

    private async _uploadFile(file: File): Promise<void> {
        if (!this._checkFile(file)) {
            MessageWindow.showAlert(new LocalizedStringBuilder("GUI-781249")); // Tento soubor nelze použít jako avatar.
            return;
        }
        const oldValue = this._imgSrc;
        this._imgSrc = null;

        this._isError = false;
        this._isLoading = true;
        this._uploadProgress = 0;
        this._loaderType = LoaderType.progressBar;
        const streamReader = new FileStreamReader(file);

        const uploader = new FileUploader();
        uploader.onFinished.add(async (_fu, dmsTempId) => {
            try {
                this._loaderType = LoaderType.spinner;
                await this._saveAvatarData(dmsTempId);
                this.__imgSrc = URL.createObjectURL(file);
            } catch (e) {
                this.__imgSrc = oldValue;
            } finally {
                this._isLoading = false;
            }
        });

        uploader.onError.add(() => {
            this._isError = true;
            this._isLoading = false;
        });

        uploader.onProgress.add((_, progress) => {
            this._uploadProgress = progress;
            this._render();
        });

        return uploader.start(streamReader, () => OWManager.getDocumentUploadUrl(FileUtils.getFileExtension(streamReader.name), streamReader.size));
    }

    private async _deleteAvatar(): Promise<void> {
        const { result } = await MessageWindow.showYesNoDialog(new LocalizedStringBuilder("GUI-780208"));
        if (result === MessageButtonType.ok) {
            this._isLoading = true;
            try {
                await this._saveAvatarData(null);

                this.__imgSrc = EMPTY_AVATAR_PATH;
            } finally {
                this._isLoading = false;
            }
        }
    }

    private _checkFile(file: File): boolean {
        return (file.type.startsWith("image/") && IMAGE_FILE_EXTENSIONS.some(item => file.name.endsWith(`.${item}`)));
    }

    private async _getAvatarUrl(): Promise<string> {
        if (this._avatarUrl) return this._avatarUrl;

        this._avatarUrl = await OWManager.getAvatarUrl(this._userId ?? this._currentUser.id);

        return this._avatarUrl;
    }

    private async _getAvatarData(): Promise<IUserProfileData> {
        try {
            return JSON.parse((await this._actualObject.getValue(this._attrName))?.valueOf()) ?? {};
        } catch (e) {
            // spatne ulozeny JSON ignorovat a vratit prazdne
            if (e instanceof SyntaxError) return {};
            throw e;
        }
    }

    private async _saveAvatarData(newData: string): Promise<void> {
        const sendingData = await this._getAvatarData() as IUserProfileDataNewData;
        sendingData.new = newData;
        await this._actualObject.setValue(this._attrName, JSON.stringify(sendingData));
        await this._actualObject.applyUpdates();
    }
}
